import { useState } from 'react'
import type { SiteAnnouncementBootstrap } from '../../shared/siteAnnouncement'

const BOOTSTRAP_ID = 'sg-site-announcement-bootstrap'

function readBootstrap(): SiteAnnouncementBootstrap {
  if (typeof document === 'undefined') return { active: false, announcement: null }
  const node = document.getElementById(BOOTSTRAP_ID)
  if (!node?.textContent) return { active: false, announcement: null }

  try {
    const parsed = JSON.parse(node.textContent) as Partial<SiteAnnouncementBootstrap>
    if (parsed.active !== true || !parsed.announcement || typeof parsed.announcement.message !== 'string') {
      return { active: false, announcement: null }
    }
    return {
      active: true,
      announcement: {
        message: parsed.announcement.message,
        promoCode: typeof parsed.announcement.promoCode === 'string' ? parsed.announcement.promoCode : null,
        ctaLabel: typeof parsed.announcement.ctaLabel === 'string' ? parsed.announcement.ctaLabel : null,
        ctaUrl: typeof parsed.announcement.ctaUrl === 'string' ? parsed.announcement.ctaUrl : null,
      },
    }
  } catch {
    return { active: false, announcement: null }
  }
}

async function copyPromoCode(value: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value)
      return true
    } catch {
      // Fall through to the legacy copy path.
    }
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = value
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const copied = document.execCommand('copy')
    textarea.remove()
    return copied
  } catch {
    return false
  }
}

function isExternalUrl(value: string) {
  try {
    return new URL(value, window.location.origin).origin !== window.location.origin
  } catch {
    return false
  }
}

export default function SiteAnnouncement() {
  const [bootstrap] = useState<SiteAnnouncementBootstrap>(() => readBootstrap())
  const [copyStatus, setCopyStatus] = useState('')

  if (!bootstrap.active || !bootstrap.announcement) return null

  const { message, promoCode, ctaLabel, ctaUrl } = bootstrap.announcement
  const externalCta = ctaUrl ? isExternalUrl(ctaUrl) : false

  const copy = async () => {
    if (!promoCode) return
    const copied = await copyPromoCode(promoCode)
    if (!copied) {
      setCopyStatus('Copy unavailable. Select the code to copy it.')
      return
    }
    setCopyStatus('Copied!')
    window.setTimeout(() => setCopyStatus(''), 1800)
  }

  return (
    <aside className="sg-site-announcement" aria-label="Site announcement">
      <div className="sg-site-announcement__inner">
        <span className="sg-site-announcement__message">{message}</span>

        {promoCode ? (
          <button
            className="sg-site-announcement__code"
            type="button"
            onClick={() => void copy()}
            aria-label={`Copy promo code ${promoCode}`}
            title="Copy promo code"
          >
            <span>{promoCode}</span>
            <span className="sg-site-announcement__copy-hint" aria-hidden="true">COPY</span>
          </button>
        ) : null}

        {ctaLabel && ctaUrl ? (
          <a
            className="sg-site-announcement__cta"
            href={ctaUrl}
            target={externalCta ? '_blank' : undefined}
            rel={externalCta ? 'noopener noreferrer' : undefined}
          >
            {ctaLabel}
          </a>
        ) : null}

        <span className="sg-site-announcement__status sr-only" role="status" aria-live="polite" aria-atomic="true">
          {copyStatus}
        </span>
      </div>
    </aside>
  )
}
